/**
 * Étrendi aláírás — the dietary sign-off of handover rev7 §11.7, the K8 of
 * §11.5 and the rule in §11.4 that an order with a note is not approved until
 * the allergen signatory has signed it off.
 *
 * The resolution is mandatory, because §11.6 prints it on the production
 * order, and a signature with a blank resolution would print as a blank line
 * on a document the kitchen works from.
 *
 * The allergen data beside the note is example data until Robi signs the
 * catalogue off (§7.3), and the screen says so on every order rather than
 * once at the top.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'

import { asDate, fmt, Loading, useConsoleAction, useConsoleData } from '../common.tsx'

interface Line {
  dish_id: string
  name_hu: string | null
  ordered_portions: number
  is_gf_portion: boolean
  allergens: { code: number | string; name_hu?: string | null }[]
  allergen_status: string | null
  allergen_test_marked: boolean | null
}

interface DietOrder {
  id: string
  ref: string
  status: string
  event_date: string
  guests: number
  gf_guests: number
  customer_name: string
  dietary_note: string | null
  lines: Line[]
  dietary_signoff:
    | { decision: string; resolution: string; at: string; signed_by: { name?: string } }
    | null
}

export function Dietary(): JSX.Element {
  const { data, error, loading, reload } = useConsoleData<{ orders: DietOrder[] }>(
    'dietary_queue',
    {},
  )
  const { run, busy, error: actionError } = useConsoleAction()
  const [resolution, setResolution] = useState<Record<string, string>>({})

  async function decide(order: DietOrder, decision: 'signed' | 'rejected'): Promise<void> {
    const text = resolution[order.id] ?? ''
    if (await run('dietary_sign_off', { order_id: order.id, decision, resolution: text })) {
      setResolution((previous) => ({ ...previous, [order.id]: '' }))
      reload()
    }
  }

  return (
    <section data-testid="view-dietary">
      <h2>Étrendi aláírás</h2>
      <p className="lead">
        §11.4: az étrendi megjegyzéssel érkező rendelést az allergén aláíró hagyja jóvá, mielőtt
        a catering jóváhagyná. A döntés indoklása rákerül a gyártási rendelésre (§11.6).
      </p>

      <Loading loading={loading} error={error} />
      {actionError !== null && <p className="note error" data-testid="action-error">{actionError}</p>}

      {data !== null && data.orders.length === 0 && (
        <p className="note" data-testid="dietary-empty">Nincs aláírásra váró étrendi megjegyzés.</p>
      )}

      {data?.orders.map((order) => {
        const unverified = order.lines.some(
          (line) => line.allergen_status !== 'verified' || line.allergen_test_marked === true,
        )
        return (
          <article className="card" key={order.id} data-testid={`dietary-${order.ref}`}>
            <h3>
              <Link to={`/staff/orders/${order.id}`}>{order.ref}</Link>
              {' · '}
              {asDate(order.event_date, true)}
              {' · '}
              {fmt.n(order.guests)} fő
              {order.gf_guests > 0 ? ` (${fmt.n(order.gf_guests)} gluténmentes)` : ''}
            </h3>

            <p className="note">{order.dietary_note}</p>

            {unverified && (
              <p className="note gap" data-testid={`example-allergens-${order.ref}`}>
                Az alábbi allergénadatok ELLENŐRZÉSRE VÁRNAK: példaadatok, nem a konyha aláírt
                listája (§7.3).
              </p>
            )}

            <table>
              <thead>
                <tr>
                  <th>Fogás</th>
                  <th className="num">Adag</th>
                  <th>Allergének</th>
                </tr>
              </thead>
              <tbody>
                {order.lines.map((line) => (
                  <tr key={line.dish_id}>
                    <td>
                      {line.name_hu ?? line.dish_id}
                      {line.is_gf_portion && <span className="tag ok">gluténmentes</span>}
                    </td>
                    <td className="num">{fmt.n(line.ordered_portions)}</td>
                    <td>
                      {line.allergens.length === 0
                        ? 'nincs jelölt allergén'
                        : line.allergens
                          .map((a) => `${a.code} ${a.name_hu ?? ''}`.trim())
                          .join(' · ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {order.dietary_signoff !== null && (
              <p className="note" data-testid={`signoff-${order.ref}`}>
                <strong>
                  {order.dietary_signoff.decision === 'signed' ? 'Jóváhagyva' : 'Elutasítva'}
                </strong>{' '}
                — {order.dietary_signoff.resolution} ·{' '}
                {order.dietary_signoff.signed_by?.name ?? ''} · {asDate(order.dietary_signoff.at)}
              </p>
            )}

            <label htmlFor={`res-${order.id}`}>Indoklás (kötelező, a gyártási rendelésre kerül)</label>
            <textarea
              id={`res-${order.id}`}
              rows={2}
              value={resolution[order.id] ?? ''}
              onChange={(event) =>
                setResolution((previous) => ({ ...previous, [order.id]: event.target.value }))}
              data-testid={`resolution-${order.ref}`}
            />
            <div className="actions">
              <button
                type="button"
                className="primary"
                disabled={busy || (resolution[order.id] ?? '').trim() === ''}
                onClick={() => void decide(order, 'signed')}
                data-testid={`sign-${order.ref}`}
              >
                Aláírom
              </button>
              <button
                type="button"
                className="danger"
                disabled={busy || (resolution[order.id] ?? '').trim() === ''}
                onClick={() => void decide(order, 'rejected')}
                data-testid={`reject-${order.ref}`}
              >
                Elutasítom
              </button>
            </div>
          </article>
        )
      })}
    </section>
  )
}
